import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import '../CSS/StoreReportsTable.css';
import Sidebar from '../Component/Sidebar';
import { fetchStoreReportsThunk } from '../features/stores/storeThunks';
import { clearReportsData, resetReportsError } from '../features/stores/storeSlice';
import { isDeptHeadOrAbove } from '../RoleBasedRoute';

const StoreReportsTable = () => {
  // Redux hooks
  const dispatch = useDispatch();
  const { reportsData } = useSelector(state => state.stores);
  const { user } = useSelector(state => state.auth);
  const isAdmin = isDeptHeadOrAbove(user);
  const { stores, loading, error, filters } = reportsData;

  // State for filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [includeInactive, setIncludeInactive] = useState(false);

  // Debounced search to avoid too many API calls
  const [searchDebounced, setSearchDebounced] = useState('');

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounced(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch data when filters change
  const fetchData = useCallback(() => {
    dispatch(fetchStoreReportsThunk({
      startDate,
      endDate,
      search: searchDebounced
    }));
  }, [dispatch, startDate, endDate, searchDebounced]);

  // Initial data fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      dispatch(clearReportsData());
    };
  }, [dispatch]);

  // Clear error when user interacts
  const handleClearError = () => {
    dispatch(resetReportsError());
  };

  // Filtered and sorted data
  const filteredAndSortedData = useMemo(() => {
    let filtered = [...stores];

    // Inactive store filter — admins can opt-in; everyone else sees active only
    if (!(isAdmin && includeInactive)) {
      filtered = filtered.filter(store => store.is_active !== false);
    }

    // Client-side search filtering (in addition to server-side search)
    if (searchTerm && !searchDebounced) {
      filtered = filtered.filter(store => {
        const matchesSearch = 
          store.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          store.category.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesSearch;
      });
    }

    // Sorting
    if (sortField) {
      filtered.sort((a, b) => {
        let aVal = a[sortField];
        let bVal = b[sortField];
        
        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }
        
        if (sortDirection === 'asc') {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      });
    }

    return filtered;
  }, [stores, searchTerm, searchDebounced, sortField, sortDirection, isAdmin, includeInactive]);

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Handle date changes
  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
  };

  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
  };

  // Function to export data to Excel
  const exportToExcel = () => {
    if (filteredAndSortedData.length === 0) {
      alert('No data to export');
      return;
    }

    // Prepare data for export
    const exportData = filteredAndSortedData.map(store => ({
      'Store ID': store.id,
      'Store Name': store.name,
      'Category': store.category,
      'Theft Reported': store.theft_reported,
      'Theft Prevented': store.theft_prevented,
      'Incident Reported': store.incident_reported,
      'Cashier Suspicious': store.cashier_suspicious,
      'Cashier Fraud': store.cashier_fraud
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Store Reports");
    
    // Generate Excel file buffer
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    
    // Create Blob and save file
    const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
    const dateRange = `${startDate}_to_${endDate}`;
    saveAs(data, `Store_Reports_${dateRange}.xlsx`);
  };

  // Function to refresh data
  const handleRefresh = () => {
    fetchData();
  };

  // Get the appropriate team color for category
  const getCategoryStyle = (category) => {
    const categoryLower = category.toLowerCase();
    if (categoryLower.includes('team c')) {
      return { background: '#E3F2FD', color: '#1976D2', border: '1px solid #90CAF9' };
    } else if (categoryLower.includes('team e')) {
      return { background: '#F3E5F5', color: '#7B1FA2', border: '1px solid #CE93D8' };
    } else if (categoryLower.includes('team g')) {
      return { background: '#E8F5E9', color: '#388E3C', border: '1px solid #81C784' };
    } else {
      return { background: '#F5F5F5', color: '#666', border: '1px solid #E0E0E0' };
    }
  };

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    return filteredAndSortedData.reduce((acc, store) => ({
      totalTheftReported: acc.totalTheftReported + store.theft_reported,
      totalTheftPrevented: acc.totalTheftPrevented + store.theft_prevented,
      totalIncidents: acc.totalIncidents + store.incident_reported,
      totalSuspicious: acc.totalSuspicious + store.cashier_suspicious,
      totalFraud: acc.totalFraud + store.cashier_fraud
    }), {
      totalTheftReported: 0,
      totalTheftPrevented: 0,
      totalIncidents: 0,
      totalSuspicious: 0,
      totalFraud: 0
    });
  }, [filteredAndSortedData]);

  return (
    <div className='index-box'>
      <Sidebar />

      <div className="store-reports-container">
        {/* Header */}
        <div className="store-reports-header">
          <div className="store-reports-header-content">
            <h1 className="store-reports-title">Store Reports Dashboard</h1>
            <p className="store-reports-subtitle">Monitor and analyze store incidents and reports</p>
          </div>
        </div>
        
        {/* Error Display */}
        {error && (
          <div className="store-reports-error-banner">
            <svg className="store-reports-error-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            </svg>
            <span>{error}</span>
            <button onClick={handleClearError} className="store-reports-error-close">×</button>
          </div>
        )}


        
        {/* Filters Row */}
        <div className="store-reports-filters">
          <div className="store-reports-filters-left">
            <div className="store-reports-date-range-group">
              <label className="store-reports-filter-label">From:</label>
              <input 
                type="date" 
                className="store-reports-date-input"
                value={startDate}
                onChange={handleStartDateChange}
                disabled={loading}
              />
              
              <label className="store-reports-filter-label">To:</label>
              <input 
                type="date" 
                className="store-reports-date-input"
                value={endDate}
                onChange={handleEndDateChange}
                disabled={loading}
              />
            </div>
            
            <div className="store-reports-search-group">
              <svg className="store-reports-search-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
              </svg>
              <input 
                type="text" 
                placeholder="Search stores or categories..." 
                className="store-reports-search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>

          <div className="store-reports-filters-right">
            {isAdmin && (
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', color: '#f59e0b', fontWeight: 500, whiteSpace: 'nowrap' }}>
                <input
                  type="checkbox"
                  checked={includeInactive}
                  onChange={(e) => setIncludeInactive(e.target.checked)}
                  disabled={loading}
                />
                Include inactive stores
              </label>
            )}
            <button 
              className="store-reports-action-btn store-reports-refresh-btn"
              onClick={handleRefresh}
              disabled={loading}
              title="Refresh data"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
              </svg>
              Refresh
            </button>

            <button 
              className="store-reports-action-btn store-reports-export-btn"
              onClick={exportToExcel}
              disabled={loading || filteredAndSortedData.length === 0}
              title="Export filtered data to Excel"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 12v7H5v-7H3v7c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2zm-6 .67l2.59-2.58L17 11.5l-5 5-5-5 1.41-1.41L11 12.67V3h2z"/>
              </svg>
              Export ({filteredAndSortedData.length})
            </button>
          </div>
        </div>
        
        {/* Loading Indicator */}
        {loading && (
          <div className="store-reports-loading">
            <div className="store-reports-loading-spinner"></div>
            <p>Loading store reports data...</p>
          </div>
        )}
        
        {/* Data Table */}
        {!loading && (
          <div className="store-reports-table-wrapper">
            <div className="store-reports-table-container">
              <table className="store-reports-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('name')} className="sortable">
                      <div className="store-reports-th-content">
                        <span>Store Name</span>
                        {sortField === 'name' && (
                          <svg className={`store-reports-sort-icon ${sortDirection}`} viewBox="0 0 24 24" fill="currentColor">
                            <path d="M7 10l5 5 5-5z"/>
                          </svg>
                        )}
                      </div>
                    </th>
                    <th onClick={() => handleSort('category')} className="sortable">
                      <div className="store-reports-th-content">
                        <span>Category</span>
                        {sortField === 'category' && (
                          <svg className={`store-reports-sort-icon ${sortDirection}`} viewBox="0 0 24 24" fill="currentColor">
                            <path d="M7 10l5 5 5-5z"/>
                          </svg>
                        )}
                      </div>
                    </th>
                    <th onClick={() => handleSort('theft_reported')} className="sortable numeric">
                      <div className="store-reports-th-content">
                        <span>Theft Reported</span>
                        {sortField === 'theft_reported' && (
                          <svg className={`store-reports-sort-icon ${sortDirection}`} viewBox="0 0 24 24" fill="currentColor">
                            <path d="M7 10l5 5 5-5z"/>
                          </svg>
                        )}
                      </div>
                    </th>
                    <th onClick={() => handleSort('theft_prevented')} className="sortable numeric">
                      <div className="store-reports-th-content">
                        <span>Theft Prevented</span>
                        {sortField === 'theft_prevented' && (
                          <svg className={`store-reports-sort-icon ${sortDirection}`} viewBox="0 0 24 24" fill="currentColor">
                            <path d="M7 10l5 5 5-5z"/>
                          </svg>
                        )}
                      </div>
                    </th>
                    <th onClick={() => handleSort('incident_reported')} className="sortable numeric">
                      <div className="store-reports-th-content">
                        <span>Incident Reported</span>
                        {sortField === 'incident_reported' && (
                          <svg className={`store-reports-sort-icon ${sortDirection}`} viewBox="0 0 24 24" fill="currentColor">
                            <path d="M7 10l5 5 5-5z"/>
                          </svg>
                        )}
                      </div>
                    </th>
                    <th onClick={() => handleSort('cashier_suspicious')} className="sortable numeric">
                      <div className="store-reports-th-content">
                        <span>Cashier Suspicious</span>
                        {sortField === 'cashier_suspicious' && (
                          <svg className={`store-reports-sort-icon ${sortDirection}`} viewBox="0 0 24 24" fill="currentColor">
                            <path d="M7 10l5 5 5-5z"/>
                          </svg>
                        )}
                      </div>
                    </th>
                    <th onClick={() => handleSort('cashier_fraud')} className="sortable numeric">
                      <div className="store-reports-th-content">
                        <span>Cashier Fraud</span>
                        {sortField === 'cashier_fraud' && (
                          <svg className={`store-reports-sort-icon ${sortDirection}`} viewBox="0 0 24 24" fill="currentColor">
                            <path d="M7 10l5 5 5-5z"/>
                          </svg>
                        )}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedData.length > 0 ? (
                    filteredAndSortedData.map((store) => (
                      <tr key={store.id} className={store.is_active === false ? 'store-reports-row-inactive' : ''}>
                        <td>
                          <div className="store-reports-store-name-cell">
                            <span className="store-reports-store-id">#{store.id}</span>
                            <span className="store-reports-store-name">{store.name}</span>
                            {store.is_active === false && isAdmin && (
                              <span className="store-reports-inactive-badge" title="This store is currently inactive">
                                Inactive
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <span className="store-reports-category-badge" style={getCategoryStyle(store.category)}>
                            {store.category}
                          </span>
                        </td>
                        <td className="numeric">
                          <span className={`store-reports-metric-value ${store.theft_reported > 0 ? 'danger' : ''}`}>
                            {store.theft_reported}
                          </span>
                        </td>
                        <td className="numeric">
                          <span className={`store-reports-metric-value ${store.theft_prevented > 0 ? 'success' : ''}`}>
                            {store.theft_prevented}
                          </span>
                        </td>
                        <td className="numeric">
                          <span className={`store-reports-metric-value ${store.incident_reported > 0 ? 'warning' : ''}`}>
                            {store.incident_reported}
                          </span>
                        </td>
                        <td className="numeric">
                          <span className={`store-reports-metric-value ${store.cashier_suspicious > 0 ? 'warning' : ''}`}>
                            {store.cashier_suspicious}
                          </span>
                        </td>
                        <td className="numeric">
                          <span className={`store-reports-metric-value ${store.cashier_fraud > 0 ? 'danger' : ''}`}>
                            {store.cashier_fraud}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="store-reports-empty-state">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M20 6h-2.18c.11-.31.18-.65.18-1 0-1.66-1.34-3-3-3-1.05 0-1.96.54-2.5 1.35l-.5.67-.5-.68C10.96 2.54 10.05 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-5-2c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM9 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm11 15H4v-2h16v2zm0-5H4V8h5.08L7 10.83 8.62 12 11 8.76l1-1.36 1 1.36L15.38 12 17 10.83 14.92 8H20v6z"/>
                        </svg>
                        <p>
                          {searchTerm || searchDebounced ? 
                            'No stores found matching your search criteria.' : 
                            'No store reports data available for the selected date range.'
                          }
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {/* Footer */}
        {!loading && filteredAndSortedData.length > 0 && (
          <div className="store-reports-footer">
            <div className="store-reports-footer-info">
              <span className="store-reports-info-item">
                <strong>{filteredAndSortedData.length}</strong> of <strong>{stores.length}</strong> stores
              </span>
              {(startDate || endDate) && (
                <span className="store-reports-info-item">
                  {startDate && endDate ? `${startDate} to ${endDate}` : startDate || endDate}
                </span>
              )}
              {filters.search && (
                <span className="store-reports-info-item">
                  Search: "{filters.search}"
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StoreReportsTable;